<template>
  <div class="fitness-standard-page">
    <van-nav-bar title="体测标准查询与评分确认" fixed placeholder />

    <div v-if="isLoading" class="loading-wrapper">
      <van-loading type="spinner" color="#1989fa">标准数据加载中...</van-loading>
    </div>

    <template v-else>
      <van-dropdown-menu active-color="#1989fa">
        <van-dropdown-item v-model="filter.gender" :options="genderOptions" @change="resetResult" />
        <van-dropdown-item v-model="filter.grade" :options="gradeOptions" @change="resetResult" />
        <van-dropdown-item v-model="filter.projectCode" :options="projectOptions" @change="resetResult" />
      </van-dropdown-menu>

      <div class="score-calculator">
        <div class="section-title">我的成绩确认</div>
        <van-field
          v-model="inputScore"
          type="number"
          center
          clearable
          label="实际成绩"
          placeholder="请输入成绩数字"
        >
          <template #button>
            <van-button size="small" type="info" @click="calculateScore">一键确认得分</van-button>
          </template>
        </van-field>

        <div class="result-card" v-if="queryResult">
          <div class="result-content">
            <div class="result-item">
              <span>项目:</span> <strong>{{ getProjectName(filter.projectCode) }}</strong>
            </div>
            <div class="result-item">
              <span>得分:</span> <strong class="score-text">{{ queryResult.score }} 分</strong>
            </div>
            <div class="result-item">
              <span>等级:</span>
              <van-tag :type="getLevelTagType(queryResult.level)" size="medium">{{ queryResult.level }}</van-tag>
            </div>
          </div>
        </div>
      </div>

      <div class="standard-table-container">
        <div class="section-title">国家体测评分标准表</div>
        <div v-if="filteredStandards.length > 0" class="table-wrapper">
          <table class="standard-table">
            <thead>
              <tr>
                <th>成绩区间</th>
                <th>得分</th>
                <th>评定等级</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="(item, index) in filteredStandards" 
                :key="index" 
                :class="{ 'highlight-row': queryResult && queryResult.score === item.score }"
              >
                <td>{{ formatRange(item.range) }}</td>
                <td><span class="score-val">{{ item.score }}</span></td>
                <td>
                  <van-tag :type="getLevelTagType(item.level)" plain>{{ item.level }}</van-tag>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <van-empty v-else description="暂无该筛选条件下的评分标准" />
      </div>
    </template>
  </div>
</template>

<script>
// 引入 papaparse 库，用于解析 CSV
import Papa from 'papaparse';

export default {
  name: 'FitnessStandard',
  data() {
    return {
      isLoading: true, // 控制加载状态
      standardData: [], // 存储解析后的所有 CSV 数据

      // 筛选条件 (默认选中男生、11年级、肺活量)
      filter: {
        gender: 0,
        grade: 11,
        projectCode: 'VitalCapacity',
      },
      inputScore: '',
      queryResult: null,

      // 下拉框选项 (可根据实际业务继续扩充)
      genderOptions: [
        { text: '男生标准', value: 0 },
        { text: '女生标准', value: 1 },
      ],
      gradeOptions: [
        { text: '小学一年级 (11)', value: 11 },
        { text: '小学二年级 (12)', value: 12 },
        { text: '小学三年级 (13)', value: 13 },
        { text: '小学四年级 (14)', value: 14 },
        { text: '小学五年级 (15)', value: 15 },
        { text: '小学六年级 (16)', value: 16 },
      ],
      projectOptions: [
        { text: '肺活量', value: 'VitalCapacity' },
        { text: '立定跳远', value: 'Jump' },
          { text: '引体向上', value: 'PullUp' },
        { text: '坐位体前屈', value: 'SitAndReach' },
        { text: '仰卧起坐', value: 'SitUp' },
        { text: '体重/身高', value: 'HeightWeight' },
        { text: '双杠臂屈伸', value: 'TricepsDip' },
        { text: '纵跳摸高', value: 'TricepsDip' },
        // 若有其他项目，例如：
        // { text: '50米跑', value: 'Run50m' },
      ],
    };
  },
  computed: {
    // 动态过滤当前选择下的标准，并按得分从高到低排序
    filteredStandards() {
      return this.standardData
        .filter(
          (item) =>
            item.gender === this.filter.gender &&
            item.grade === this.filter.grade &&
            item.projectCode === this.filter.projectCode
        )
        .sort((a, b) => b.score - a.score); 
    },
  },
  created() {
    // 页面初始化时触发加载 CSV
    this.loadCSVData();
  },
  methods: {
    // 核心：使用 PapaParse 动态加载 public/standard.csv
    // 核心：使用 PapaParse 动态加载 public/standard.csv
    loadCSVData() {
      Papa.parse('/standard.csv', {
        download: true, 
        header: false, // 【重点1】关闭 header 匹配，改用数组下标，100% 避免表头匹配失败
        skipEmptyLines: true,
        encoding: 'GBK', // 【备用提示】如果下面解析出的中文是乱码，请取消这行的注释
        complete: (results) => {
            console.log(results);
            
          const rawData = results.data;
          
          // 【重点2】移除第一行表头 (判断第3列"年级"如果是文字就移除)
          if (rawData.length > 0 && isNaN(parseInt(rawData[0][2]))) {
            rawData.shift();
          }

          // 根据您 CSV 的列顺序，通过下标(0, 1, 2...)精准获取对应数据
          // 第0列:项目编码, 第1列:项目名称, 第2列:年级, 第3列:成绩区间, 第4列:得分, 第5列:等级, 第6列:性别
          this.standardData = rawData.map(row => {
            return {
              projectCode: row[0],            // 第0列: 项目编码 (如 'VitalCapacity')
              grade: parseInt(row[2]),        // 第2列: 年级 (如 11)
              range: row[3],                  // 第3列: 成绩区间 (如 '1700~9999999')
              score: parseFloat(row[4]),      // 第4列: 得分 (如 100)
              level: row[5],                  // 第5列: 等级 (如 '优秀')
              gender: parseInt(row[6])        // 第6列: 性别 (如 0 或 1)
            };
          });
          
          console.log("解析成功，前三条数据:", this.standardData.slice(0, 3));
          this.isLoading = false;
        },
        error: (err) => {
          console.error("CSV解析失败:", err);
          this.$toast.fail('标准数据加载失败，请检查文件');
          this.isLoading = false;
        }
      });
    },

    // 切换筛选条件时，重置计算结果
    resetResult() {
      this.queryResult = null;
      this.inputScore = '';
    },
    
    // 工具函数：获取项目中文名
    getProjectName(code) {
      const proj = this.projectOptions.find((p) => p.value === code);
      return proj ? proj.text : code;
    },
    
    // 工具函数：格式化展示区间
    formatRange(rangeStr) {
      if (!rangeStr) return '';
      let [min, max] = rangeStr.split('~');
      // 处理极限值使其对用户更直观
      if (max.includes('999999')) return `≥ ${min}`;
      if (min === '0') return `≤ ${max}`;
      return `${min} ~ ${max}`;
    },
    
    // 工具函数：根据等级匹配 Tag 颜色
    getLevelTagType(level) {
      switch (level) {
        case '优秀': return 'success';
        case '良好': return 'primary';
        case '及格': return 'warning';
        case '不及格': return 'danger';
        default: return 'default';
      }
    },
    
    // 核心逻辑：确认实际成绩并匹配得分
    calculateScore() {
      if (!this.inputScore) {
        this.$toast('请先输入实际成绩');
        return;
      }

      const scoreVal = parseFloat(this.inputScore);
      let matchedItem = null;

      // 遍历当前条件下的评分标准
      for (let i = 0; i < this.filteredStandards.length; i++) {
        const item = this.filteredStandards[i];
        const [minStr, maxStr] = item.range.split('~');
        const min = parseFloat(minStr);
        const max = parseFloat(maxStr);

        // 区间判断：[min, max)
        // 如果用户的成绩处于该区间内，或者是极值情况，则判定为该项得分
        if (scoreVal >= min && scoreVal < max) {
          matchedItem = item;
          break;
        } else if (max >= 999999 && scoreVal >= min) {
          // 触及上限最高分
          matchedItem = item;
          break;
        }
      }

      if (matchedItem) {
        this.queryResult = matchedItem;
        this.$toast.success('评分确认成功');
      } else {
        this.queryResult = null;
        this.$toast.fail('未匹配到评分标准，请检查成绩或筛选条件');
      }
    },
  },
};
</script>

<style scoped>
.fitness-standard-page {
  background-color: #f7f8fa;
  min-height: 100vh;
  padding-bottom: 30px;
}

.loading-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #969799;
}

.section-title {
  font-size: 14px;
  color: #969799;
  padding: 16px 16px 8px;
}

.score-calculator {
  background-color: #fff;
  margin-top: 10px;
  padding-bottom: 16px;
}

.result-card {
  margin: 16px 16px 0;
  padding: 16px;
  background: #f0f7ff;
  border-radius: 8px;
  border: 1px solid #cce4ff;
}

.result-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.result-item {
  font-size: 14px;
  color: #323233;
  display: flex;
  align-items: center;
}

.result-item span {
  color: #969799;
  margin-right: 6px;
}

.score-text {
  color: #ee0a24;
  font-size: 18px;
}

.standard-table-container {
  margin-top: 10px;
  background-color: #fff;
  padding-bottom: 16px;
}

.table-wrapper {
  padding: 0 16px;
}

.standard-table {
  width: 100%;
  border-collapse: collapse;
  text-align: center;
  font-size: 13px;
  color: #323233;
}

.standard-table th,
.standard-table td {
  border: 1px solid #ebedf0;
  padding: 10px 4px;
}

.standard-table th {
  background-color: #f7f8fa;
  color: #646566;
  font-weight: 500;
}

.score-val {
  font-weight: bold;
  color: #1989fa;
}

/* 高亮选中行 */
.highlight-row {
  background-color: #e8f3fe !important;
  transition: background-color 0.3s;
}
</style>